import { NavLink, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { kanbanApi } from '../shared/api/kanban';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

/* ---------------- Inline icons (stroke 1.5, currentColor) ---------------- */
const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const HomeIcon = () => (
  <svg {...iconProps}>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ContactsIcon = () => (
  <svg {...iconProps}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CompaniesIcon = () => (
  <svg {...iconProps}>
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
    <path d="M9 9h.01M9 13h.01M9 17h.01" />
  </svg>
);

const PipelineIcon = () => (
  <svg {...iconProps}>
    <path d="M3 4h6l3 4h9v10H3z" />
    <path d="M3 9h18" />
  </svg>
);

const BoardsIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </svg>
);

const MyCardsIcon = () => (
  <svg {...iconProps}>
    <path d="M4 4h12v4H4z" />
    <path d="M4 10h16v4H4z" />
    <path d="M4 16h10v4H4z" />
  </svg>
);

const ContractsIcon = () => (
  <svg {...iconProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

const ReportsIcon = () => (
  <svg {...iconProps}>
    <path d="M3 3v18h18" />
    <path d="M7 14l3-3 3 3 4-5" />
  </svg>
);

const QualificationIcon = () => (
  <svg {...iconProps}>
    <path d="M9 12l2 2 4-4" />
    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.5 0 4.76 1.02 6.4 2.66" />
  </svg>
);

const SettingsIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <HomeIcon />, end: true },
  { to: '/crm/contacts', label: 'Contacts', icon: <ContactsIcon /> },
  { to: '/crm/companies', label: 'Companies', icon: <CompaniesIcon /> },
  { to: '/pipeline', label: 'Pipeline', icon: <PipelineIcon /> },
  { to: '/boards', label: 'Boards', icon: <BoardsIcon /> },
  { to: '/my-cards', label: 'My cards', icon: <MyCardsIcon /> },
  { to: '/contracts', label: 'Contracts', icon: <ContractsIcon /> },
  { to: '/reports', label: 'Reports', icon: <ReportsIcon /> },
  { to: '/qualification', label: 'Qualification', icon: <QualificationIcon /> },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

const COLLAPSE_BREAKPOINT = 1100;
const MOBILE_BREAKPOINT = 768; // Tailwind md

export function Sidebar({
  mobileOpen,
  onClose,
}: {
  mobileOpen: boolean;
  onClose: () => void;
}) {
  // Collapse automatically below 1100px; user can also toggle manually
  const [autoCollapsed, setAutoCollapsed] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < COLLAPSE_BREAKPOINT : false,
  );
  // Below md the sidebar becomes an off-canvas drawer instead of the static
  // icon-rail, so it never steals horizontal space from the page on a phone.
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );
  const [userToggled, setUserToggled] = useState<boolean | null>(null);

  useEffect(() => {
    const onResize = () => {
      setAutoCollapsed(window.innerWidth < COLLAPSE_BREAKPOINT);
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Esc closes the mobile drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, onClose]);

  // On mobile the drawer is always full-width with labels; the icon-rail
  // collapse only applies to the static desktop layout.
  const collapsed = isMobile ? false : userToggled ?? autoCollapsed;

  return (
    <>
      {/* Mobile backdrop - tap to close. Hidden on md+ (static sidebar). */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`flex h-full flex-col surface-glass
                    fixed inset-y-0 left-0 z-50 w-72
                    transform transition-transform duration-200 ease-out
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
                    md:relative md:inset-auto md:z-auto md:translate-x-0 md:transition-[width]
                    ${collapsed ? 'md:w-[68px]' : 'md:w-60'}`}
        aria-label="Primary navigation"
      >
      {/* Brand - static (no spinning ring) so the eye can rest while working */}
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-slate-200/60 dark:border-white/10 px-4">
        <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow-brand">
          <span className="text-sm font-semibold relative z-10" style={{ fontFamily: "'Space Grotesk', Inter, sans-serif" }}>T</span>
        </div>
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50" style={{ fontFamily: "'Space Grotesk', Inter, sans-serif", letterSpacing: '-0.012em' }}>
              Tricorian Life
            </div>
            <div className="truncate text-[10px] font-medium uppercase tracking-[0.20em] text-accent-600 dark:text-accent-300/90">
              CRM
            </div>
          </div>
        )}
        {/* Close button - mobile drawer only. */}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto md:hidden btn-icon"
          aria-label="Close menu"
        >
          ✕
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-0.5">
          {NAV.map((n) =>
            n.to === '/boards' ? (
              <BoardsNavGroup key={n.to} item={n} collapsed={collapsed} onClose={onClose} />
            ) : (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.end}
                  title={collapsed ? n.label : undefined}
                  onClick={onClose}
                  className={({ isActive }) =>
                    [
                      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
                      'transition-all duration-150',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60',
                      'focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                      'dark:focus-visible:ring-offset-slate-950',
                      collapsed ? 'justify-center px-2' : '',
                      isActive
                        ? 'text-white shadow-glow-brand bg-gradient-brand'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-slate-50',
                    ].join(' ')
                  }
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    {n.icon}
                  </span>
                  {!collapsed && <span className="truncate">{n.label}</span>}
                </NavLink>
              </li>
            ),
          )}
        </ul>
      </nav>

      {/* Collapse toggle - desktop only; the mobile drawer is always full. */}
      <div className="hidden md:block shrink-0 border-t border-slate-200/60 dark:border-white/10 p-2">
        <button
          type="button"
          onClick={() => setUserToggled(!collapsed)}
          className="btn-ghost w-full justify-center"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
      </aside>
    </>
  );
}

/**
 * Boards entry that expands into a sub-list of active (non-archived) boards
 * for quick switching while inside a board. The sub-list auto-opens whenever
 * the user is on a /boards route, and the user can also manually toggle it.
 *
 * Rules:
 *  - Only ACTIVE boards (archivedAt === null) appear; archived boards are
 *    intentionally hidden from this menu.
 *  - The current board is highlighted via `:id` matching, so clicking the
 *    Boards parent still navigates to the index page (`/boards`).
 *  - Hidden when the sidebar is collapsed (we still keep the parent icon).
 */
function BoardsNavGroup({
  item,
  collapsed,
  onClose,
}: {
  item: NavItem;
  collapsed: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const params = useParams();
  // Auto-expand whenever we're anywhere under /boards. Persist user toggle
  // separately so an explicit collapse sticks while navigating boards.
  const onBoardsRoute = location.pathname.startsWith('/boards');
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = !collapsed && (userOpen ?? onBoardsRoute);

  const boardsQ = useQuery({
    queryKey: ['boards'],
    queryFn: () => kanbanApi.listBoards(),
    // Lightweight enough to keep around - sidebar reads on every page.
    staleTime: 30_000,
    enabled: !collapsed,
  });
  const activeBoards = (boardsQ.data?.items ?? []).filter((b) => !b.archivedAt);
  // Board route is `/boards/:boardId`. Match against the URL directly so
  // we don't depend on the matched route name.
  const currentBoardId = (() => {
    const m = /^\/boards\/([^/]+)/.exec(location.pathname);
    return m ? m[1] ?? null : null;
  })();
  void params;

  return (
    <li>
      <div className="flex items-center">
        <NavLink
          to={item.to}
          end
          title={collapsed ? item.label : undefined}
          onClick={onClose}
          className={({ isActive }) =>
            [
              'group relative flex flex-1 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
              'transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60',
              'focus-visible:ring-offset-2 focus-visible:ring-offset-white',
              'dark:focus-visible:ring-offset-slate-950',
              collapsed ? 'justify-center px-2' : '',
              isActive
                ? 'text-white shadow-glow-brand bg-gradient-brand'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-slate-50',
            ].join(' ')
          }
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            {item.icon}
          </span>
          {!collapsed && <span className="truncate">{item.label}</span>}
        </NavLink>
        {!collapsed && (
          <button
            type="button"
            aria-label={open ? 'Hide boards list' : 'Show boards list'}
            className="ml-1 rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition"
            onClick={() => setUserOpen(!open)}
          >
            <span
              className={`block transition-transform ${open ? '' : '-rotate-90'}`}
            >
              <ChevronDownIcon />
            </span>
          </button>
        )}
      </div>
      {open && (
        <ul className="mt-0.5 ml-3 pl-3 border-l border-slate-200 dark:border-white/[0.08] space-y-0.5">
          {boardsQ.isLoading && (
            <li className="px-2 py-1 text-[11px] text-slate-400">Loading…</li>
          )}
          {!boardsQ.isLoading && activeBoards.length === 0 && (
            <li className="px-2 py-1 text-[11px] text-slate-400">No active boards</li>
          )}
          {activeBoards.map((b) => {
            const isCurrent = b.id === currentBoardId;
            return (
              <li key={b.id}>
                <NavLink
                  to={`/boards/${b.id}`}
                  onClick={onClose}
                  className={[
                    'block rounded-md px-2 py-1 text-xs truncate transition',
                    isCurrent
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200 font-medium'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/[0.04]',
                  ].join(' ')}
                  title={b.name}
                >
                  {b.name}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
